import { useCallback, useState } from 'react'

export type OpenCIMSDrawerFn<T> = (state?: T) => void

export interface UseCIMSDrawerValues<T = unknown> {
  /**
   * Whether the drawer is visible. Use this instead of
   * {@link UseCIMSDrawerValues.state} to determine whether the retained session
   * is active.
   */
  visible: boolean
  /**
   * Optional state associated with the most recently opened drawer session.
   * Closing the drawer retains this state until the next call to
   * {@link UseCIMSDrawerValues.open} replaces it.
   */
  state: T | null
  /**
   * Opens the drawer and stores optional state for the drawer body.
   *
   * @param state Optional state to associate with this drawer session.
   */
  open: OpenCIMSDrawerFn<T>
  /**
   * Closes the drawer while retaining its session state through the close
   * animation.
   */
  close: () => void
}

/**
 * Manages the open state and optional session state for a {@link CIMSDrawer}.
 *
 * @returns values used to render and control a drawer
 */
export function useCIMSDrawer<T = unknown>(): UseCIMSDrawerValues<T> {
  const [visible, setVisible] = useState(false)
  const [state, setState] = useState<T | null>(null)

  const open = useCallback<OpenCIMSDrawerFn<T>>((state) => {
    setState(state ?? null)
    setVisible(true)
  }, [])

  const close = useCallback(() => {
    setVisible(false)
  }, [])

  return { visible, state, open, close }
}
